import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "./apiSlice";
import { selectProductListState } from "../store/productListFilterSlice";

const DEFAULT_PAGE_SIZE = 1; // Always 1

// Entity adapter
const productListAdapter = createEntityAdapter({
  selectId: (product) => product.id,
});

const initialState = productListAdapter.getInitialState({
  nextCursor: null,
  hasMore: false,
  totalCount: 0,
});

// Helper: build query string
const buildQueryString = (base, params) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, value);
    }
  });
  return `${base}?${queryParams.toString()}`;
};

// RTK Query endpoints
export const productListSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProductList: builder.query({
      query: ({ category, inventory_id, cursor = 0, filters = {} }) =>
        buildQueryString(`${category}/inventory`, {
          inventory_id,
          cursor,
          pagination: DEFAULT_PAGE_SIZE,
          ...filters,
        }),

      transformResponse: (response) => {
        const items = Array.isArray(response?.data) ? response.data : [];
        const normalized = productListAdapter.setAll({ ...initialState }, items);
        return {
          ...normalized,
          nextCursor: response?.nextCursor ?? null,
          hasMore: Boolean(response?.hasMore),
          totalCount: response?.totalCount ?? items.length,
        };
      },

      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { inventory_id, filters = {} } = queryArgs;
        return `${endpointName}(${inventory_id || "ALL"}):${JSON.stringify(filters)}`;
      },

      merge: (currentCache, incoming) => {
        if (!incoming?.ids?.length) {
          currentCache.nextCursor = incoming?.nextCursor ?? null;
          currentCache.hasMore = Boolean(incoming?.hasMore);
          return;
        }
        const incomingItems = incoming.ids.map((id) => incoming.entities[id]).filter(Boolean);
        productListAdapter.upsertMany(currentCache, incomingItems);

        currentCache.nextCursor = incoming.nextCursor;
        currentCache.hasMore = Boolean(incoming.hasMore);
        currentCache.totalCount = currentCache.ids.length;
      },

      forceRefetch({ currentArg, previousArg }) {
        return previousArg?.inventory_id !== currentArg?.inventory_id;
      },

      providesTags: (result, error, arg) =>
        result
          ? [
              { type: "ProductList", id: arg.inventory_id || "ALL" },
              ...result.ids.map((id) => ({ type: "Product", id })),
            ]
          : [{ type: "ProductList", id: arg.inventory_id || "ALL" }],
    }),

    getProductById: builder.query({
      query: ({ category, request_id }) =>
        `${category}/inventory/details?request_id=${request_id}`,
      transformResponse: (response) => response?.data ?? null,
      providesTags: (result, error, { request_id }) =>
        result ? [{ type: "Product", id: request_id }] : [],
    }),

    updateProduct: builder.mutation({
      query: ({ category, id, data }) => ({
        url: `${category}/${id}`,
        method: "PATCH",
        body: data,
      }),
      async onQueryStarted({ category, id, inventory_id, data }, { dispatch, queryFulfilled }) {
        const detailPatch = dispatch(
          productListSlice.util.updateQueryData(
            "getProductById",
            { category, request_id: id },
            (draft) => { if (draft) Object.assign(draft, data); }
          )
        );

        let listPatch;
        if (inventory_id) {
          listPatch = dispatch(
            productListSlice.util.updateQueryData(
              "getProductList",
              { category, inventory_id, cursor: 0, filters: {} },
              (draft) => {
                if (!draft?.ids) Object.assign(draft, initialState);
                productListAdapter.upsertOne(draft, { id, ...data });
                draft.totalCount = draft.ids.length;
              }
            )
          );
        }

        try {
          const { data: serverResult } = await queryFulfilled;

          if (serverResult?.id) {
            dispatch(
              productListSlice.util.updateQueryData(
                "getProductById",
                { category, request_id: serverResult.id },
                (draft) => Object.assign(draft, serverResult)
              )
            );

            const targetInventoryId = serverResult.inventory_id ?? inventory_id;
            if (targetInventoryId) {
              dispatch(
                productListSlice.util.updateQueryData(
                  "getProductList",
                  { category, inventory_id: targetInventoryId, cursor: 0, filters: {} },
                  (draft) => {
                    if (!draft?.ids) Object.assign(draft, initialState);
                    productListAdapter.upsertOne(draft, serverResult);
                    draft.totalCount = draft.ids.length;
                  }
                )
              );
            }
          }
        } catch {
          detailPatch?.undo();
          listPatch?.undo();
        }
      },

      invalidatesTags: (result) => {
        if (!result) return [];
        return [
          { type: "Product", id: result.id },
          { type: "ProductList", id: result.inventory_id || "ALL" },
        ];
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProductListQuery,
  useGetProductByIdQuery,
  useUpdateProductMutation,
} = productListSlice;

// =====================
// SELECTORS
// =====================
const selectProductListQueryResult = createSelector(
  [selectProductListState, (state) => state],
  (filter, state) => {
    const queryArgs = {
      category: filter.category,
      inventory_id: filter.inventory_id,
      cursor: filter.cursor ?? 0,
      filters: Object.fromEntries(
        Object.entries(filter).filter(
          ([key, value]) =>
            !["category", "inventory_id", "cursor", "pagination"].includes(key) &&
            value !== null &&
            value !== ""
        )
      ),
    };
    return productListSlice.endpoints.getProductList.select(queryArgs)(state);
  }
);

const selectProductListData = createSelector(
  [selectProductListQueryResult],
  (result) => result?.data ?? initialState
);

const adapterSelectors = productListAdapter.getSelectors((state) =>
  selectProductListData(state)
);

export const {
  selectAll: selectAllProducts,
  selectById: selectProductById,
  selectIds: selectProductIds,
  selectEntities: selectProductEntities,
  selectTotal: selectProductTotal,
} = adapterSelectors;

export const selectProductListPagination = createSelector(
  [selectProductListData],
  (data) => ({
    nextCursor: data.nextCursor,
    hasMore: data.hasMore,
    totalCount: data.ids?.length ?? 0,
    currentCount: data.ids?.length ?? 0,
  })
);

export const selectProductListStatus = createSelector(
  [selectProductListQueryResult],
  (result) => ({
    isLoading: result?.isLoading ?? false,
    isFetching: result?.isFetching ?? false,
    isSuccess: result?.isSuccess ?? false,
    isError: result?.isError ?? false,
    error: result?.error ?? null,
  })
);
